import type { ComponentValueResponse, FinancialSituationInput } from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as financialSituationApi from "@/lib/api/flow-instances/financial-situation";
import { useInvalidateFlowCompletion } from "./use-flow-completion";
import { goalQueryKeys } from "./use-goals";
import { usePlatformSyncWarnings } from "./use-platform-sync-warnings";
import { useInvalidatePortfolioSnapshots } from "./use-portfolio-snapshots";
import { invalidatePortfolioFamily } from "./use-portfolios";

const financialSituationKeys = {
  flowInstanceValue: (id: string | null) =>
    ["flowInstances", id, "financial-situation", "flow-instance-value"] as const,
};

export function useFinancialSituationFlowInstanceValue(id: string | null) {
  const { data, error, isLoading } = useQuery({
    queryKey: financialSituationKeys.flowInstanceValue(id),
    queryFn: () => financialSituationApi.getFlowInstanceValue(id!),
    enabled: !!id,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function useSaveFinancialSituation(instanceId: string) {
  const queryClient = useQueryClient();
  const invalidateCompletion = useInvalidateFlowCompletion();
  const invalidatePortfolioSnapshots = useInvalidatePortfolioSnapshots();
  const showPlatformSyncWarnings = usePlatformSyncWarnings();
  const queryKey = financialSituationKeys.flowInstanceValue(instanceId);
  return useMutation({
    mutationFn: (input: FinancialSituationInput) =>
      financialSituationApi.saveFlowInstanceValue(instanceId, input),
    // Optimistic update: the widget unmounts on wizard tab switch and
    // re-initializes from this cache on remount. Without it, an in-flight
    // save means the cache still holds the old value and entered data
    // "disappears" (or reappears from an older save).
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<ComponentValueResponse>(queryKey);
      queryClient.setQueryData<ComponentValueResponse>(queryKey, (old) => ({
        inputData: JSON.stringify(input),
        calculatedData: old?.calculatedData ?? null,
      }));
      return { previousData };
    },
    onError: (_err, _input, context) => {
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },
    onSuccess: (result) => {
      if (result.success && result.inputData !== undefined) {
        queryClient.setQueryData(queryKey, {
          inputData: result.inputData,
          calculatedData: result.calculatedData,
        });
        // Slice 9 — completion predicate reads totals.totalAnnualIncome / totalAssets
        // from this calculatedData blob.
        invalidateCompletion(instanceId);

        // QP-195 — a save may change the derived statuses, which re-syncs each
        // goal's platform server-side (auto-apply/clear + cascade). Surface the
        // removed-products warning and refresh the affected queries.
        const platformSync = result.platformSync;
        if (platformSync?.changed) {
          showPlatformSyncWarnings(platformSync);
          // QL-72 — the platform change may move a portfolio's calculated id or
          // clear a pick even when no snapshot moved; re-read portfolio details.
          invalidatePortfolioFamily(queryClient);
          for (const hint of platformSync.refreshHints) {
            invalidatePortfolioSnapshots(hint.portfolioId, hint);
          }
          queryClient.invalidateQueries({ queryKey: goalQueryKeys.goals(instanceId) });
        }
        // Platform eligibility depends on the derived statuses — refetch every
        // goal-platform state (goal ids unknown here; match the key prefix).
        queryClient.invalidateQueries({ queryKey: ["goalPlatforms"] });
      }
    },
  });
}
